import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchQuery, graphql } from "react-relay";
import { useHistory } from "react-router-dom";
import type { MenuPlacement, OnChangeValue } from "react-select";

import { debounce } from "lodash";
import moment from "moment";

import { CycleCategory, getCycleCategory } from "~/account/Cycles/utils";
import AppContext from "~/common/AppContext";
import type { NodesFromEdges } from "~/lib/utils";
import { environment } from "~/models";
import { ButtonLink } from "~/ui/legacyFellowIndex";
import MultiSelector from "~/ui/selectors/MultiSelector";
import Selector, { components, type MenuProps } from "~/ui/selectors/Selector";

import type { CycleListInputQuery } from "~/__generated__/CycleListInputQuery.graphql";

import styles from "./CycleListInput.module.css";

export const cycleListQuery = graphql`
	query CycleListInputQuery($archived: Boolean, $name: String) {
		viewer {
			cycles(archived: $archived, name: $name) {
				edges {
					node {
						id
						name
						default
						startDate
						endDate
					}
				}
			}
		}
	}
`;

export type CycleType = NodesFromEdges<CycleListInputQuery["response"]["viewer"]["cycles"]>;

function isNotNull<T>(item: T | undefined | null): item is NonNullable<T> {
	return item != null;
}

interface GroupCycleOption {
	label: string;
	options: CycleOption[];
}

export interface CycleOption {
	readonly value: string;
	readonly label: string;
	readonly isDefault: boolean;
}

interface CycleListInputProps {
	selected?: string[];
	onChange: (newCycle: string[]) => void;
	className?: string;
	disabled?: boolean;
	placeholder?: string;
	isMulti?: boolean;
	fixViewportOverflow?: boolean;
	menuPlacement?: MenuPlacement;
}
export default function CycleListInput({
	selected,
	onChange,
	className,
	disabled,
	placeholder,
	isMulti,
	menuPlacement,
}: CycleListInputProps) {
	const [selectValue, setSelectValue] = useState<Array<CycleOption>>([]);
	const [optionsGroups, setOptionsGroups] = useState<Array<GroupCycleOption>>([]);
	const { user } = React.useContext(AppContext);
	const hasAdminAccess = !!user?.hasAdminAccess;
	const history = useHistory();

	// We need to match the selected cycles ids with our options
	useEffect(() => {
		const allOptions = optionsGroups.flatMap(({ options }) => options);
		const selectedIds = allOptions.filter(option => selected?.includes(option.value));
		if (selectedIds.length === 0 && !isMulti) {
			return;
		}
		setSelectValue(selectedIds);
	}, [selected, optionsGroups, isMulti]);

	const selectCycle = useCallback(
		(selection: OnChangeValue<CycleOption, boolean>) => {
			if (selection) {
				if ("length" in selection) {
					const selectionValues = selection.map(({ value }) => value);
					onChange(selectionValues);
				} else {
					onChange([selection.value]);
				}
			} else {
				onChange([]);
			}
		},
		[onChange],
	);

	const createGroupedOptions = (cycles: CycleType[]) => {
		const cycleGroups: GroupCycleOption[] = [];

		const dict = new Map<CycleCategory, CycleOption[]>([
			[CycleCategory.CURRENT_CYCLE, []],
			[CycleCategory.UPCOMING_CYCLE, []],
			[CycleCategory.PAST_CYCLE, []],
		]);

		cycles.sort((a, b) => {
			// We sort the cycles by their start date, and if they're the same, we sort by their end date.
			// Null dates have less priority than non-null dates, so if date is null we set them to a far
			// away date to make the comparison easier.
			const lowPriorityDate = moment().add("100", "years").format("YYYY-MM-DD");
			const aStartDate = a.startDate || lowPriorityDate;
			const aEndDate = a.endDate || lowPriorityDate;
			const bStartDate = b.startDate || lowPriorityDate;
			const bEndDate = b.endDate || lowPriorityDate;

			if (aStartDate < bStartDate) {
				return -1;
			}
			if (aStartDate > bStartDate) {
				return 1;
			}
			if (aEndDate < bEndDate) {
				return -1;
			}
			if (aEndDate > bEndDate) {
				return 1;
			}

			return 0;
		});

		cycles.forEach(cycle => {
			const { startDate, endDate, id, name, default: isDefault } = cycle;
			const cycleOption = { label: name, value: id, isDefault };

			const cycleCategory: CycleCategory = getCycleCategory(startDate, endDate);

			if (cycleCategory === CycleCategory.CURRENT_CYCLE) {
				dict.set(CycleCategory.CURRENT_CYCLE, dict.get(CycleCategory.CURRENT_CYCLE)?.concat(cycleOption) || []);
			} else if (cycleCategory === CycleCategory.UPCOMING_CYCLE) {
				dict.set(CycleCategory.UPCOMING_CYCLE, dict.get(CycleCategory.UPCOMING_CYCLE)?.concat(cycleOption) || []);
			} else if (cycleCategory === CycleCategory.PAST_CYCLE) {
				dict.set(CycleCategory.PAST_CYCLE, dict.get(CycleCategory.PAST_CYCLE)?.concat(cycleOption) || []);
			}
		});

		for (const [key, value] of dict) {
			cycleGroups.push({
				label: key,
				options: value,
			});
		}

		return cycleGroups;
	};

	const loadOptions = useCallback(() => {
		fetchQuery<CycleListInputQuery>(
			environment,
			cycleListQuery,
			{ archived: false },
			// fetchQuery does not support store-and-network
			{ fetchPolicy: "network-only" },
		)
			.toPromise()
			.then(response => {
				let cycles: Array<CycleType> = [];
				if (response?.viewer.cycles) {
					cycles = cycles.concat(
						response.viewer.cycles.edges.filter(isNotNull).map(edge => {
							return { ...edge.node };
						}),
					);
				}

				setOptionsGroups(createGroupedOptions(cycles));
			});
	}, []);

	useEffect(() => {
		// If no cycle was preselected we automatically select the first current cycle for the single selector
		if ((!selected || !selected.length) && !isMulti) {
			// Search for the default cycle in all options
			const defaultCycle = optionsGroups.flatMap(group => group.options).find(option => option.isDefault);
			if (defaultCycle) {
				onChange([defaultCycle.value]);
				return;
			}

			const currentCycles = optionsGroups
				.find(group => group.label === CycleCategory.CURRENT_CYCLE)
				?.options.map(({ value }) => value);

			if (currentCycles?.length) {
				onChange([currentCycles[0]]);
				return;
			}
			// If the account doesn't have any current cycles we try to use the first
			// Upcoming cycle or the first past cycle
			const upcomingCycles = optionsGroups
				.find(group => group.label === CycleCategory.UPCOMING_CYCLE)
				?.options.map(({ value }) => value);
			if (upcomingCycles?.length) {
				onChange([upcomingCycles[0]]);
				return;
			}
			const pastCycles = optionsGroups
				.find(group => group.label === CycleCategory.PAST_CYCLE)
				?.options.map(({ value }) => value);
			if (pastCycles?.length) {
				onChange([pastCycles[0]]);
				return;
			}
		}
	}, [isMulti, onChange, optionsGroups, selected]);

	const debounceLoadOptions = useMemo(() => debounce(loadOptions, 500), [loadOptions]);

	useEffect(() => {
		debounceLoadOptions();
	}, [debounceLoadOptions]);

	const navigateToManageCyclesPage = useCallback(() => {
		history.push("/account/cycles/");
	}, [history]);

	const menuLink = useMemo(() => {
		return { text: "Manage cycles", onClick: navigateToManageCyclesPage };
	}, [navigateToManageCyclesPage]);

	const SelectorMenu = React.useCallback(
		<T extends {}>({ children, ...rest }: MenuProps<T, false>) => (
			<components.Menu {...rest}>
				{children}
				{menuLink && (
					<ButtonLink onClick={menuLink?.onClick} className={styles.menuButtonLink}>
						{menuLink?.text}
					</ButtonLink>
				)}
			</components.Menu>
		),
		[menuLink],
	);

	const MultiSelectorMenu = React.useCallback(
		<T extends {}>({ children, ...rest }: MenuProps<T, true>) => {
			const { clearValue } = rest;

			return (
				<components.Menu {...rest}>
					{children}
					<ButtonLink onClick={menuLink?.onClick ?? clearValue} className={styles.menuButtonLink}>
						{menuLink?.text ?? "Clear Selection"}
					</ButtonLink>
				</components.Menu>
			);
		},
		[menuLink],
	);

	return isMulti ? (
		<MultiSelector
			placeholder={placeholder || "Current cycles"}
			options={optionsGroups}
			value={selectValue}
			onChange={selectCycle}
			className={className}
			isDisabled={disabled}
			components={hasAdminAccess ? { Menu: MultiSelectorMenu } : {}}
			menuSide={menuPlacement === "auto" ? "bottom" : menuPlacement}
		/>
	) : (
		<Selector
			placeholder={placeholder || "Select a cycle"}
			options={optionsGroups}
			value={selectValue}
			onChange={selectCycle}
			className={className}
			isDisabled={disabled}
			components={hasAdminAccess ? { Menu: SelectorMenu } : {}}
			menuPlacement={menuPlacement}
			isSearchable
		/>
	);
}
